import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Input, Popconfirm, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  deleteDriverMutation,
  getDriversOptions,
  getDriversQueryKey,
} from "../../../services/client/@tanstack/react-query.gen";
import type { DriverResponse } from "../../../services/client/types.gen";
import { useNotify } from "../../util-components/Notifications/notifications";
import { DisableForViewer } from "../../common/DisableForViewer";
import { extractErrorMessage } from "../../../util/extractErrorMessage";
import { CreateDriverModal } from "./CreateDriverModal";
import { DriverCsvUploadModal } from "./DriverCsvUploadModal";

type DriversTabProps = {
  site_id: string;
  /** Partner of the site; new drivers are created under it. */
  partner_id?: string;
};

const formatVehicle = (car: DriverResponse["car"]) =>
  car ? `${car.make} ${car.model} (${car.year})` : "-";

export const DriversTab: React.FC<DriversTabProps> = ({
  site_id,
  partner_id,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);

  const driversOptions = { query: { site_ids: [site_id] } };
  const {
    data: drivers,
    isLoading,
    error,
  } = useQuery(getDriversOptions(driversOptions));

  const deleteMutation = useMutation({
    ...deleteDriverMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getDriversQueryKey(driversOptions),
      });
      notify({ type: "success", message: "Driver deleted" });
    },
    onError: (error) => {
      notify({
        type: "error",
        message: "Failed to delete driver",
        description: extractErrorMessage(error),
      });
    },
  });

  const filteredDrivers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return drivers ?? [];
    }
    return (drivers ?? []).filter(
      (driver) =>
        driver.email.toLowerCase().includes(query) ||
        formatVehicle(driver.car).toLowerCase().includes(query) ||
        (driver.badge_ids ?? []).some((badgeId) =>
          badgeId.toLowerCase().includes(query),
        ),
    );
  }, [drivers, search]);

  const columns: ColumnsType<DriverResponse> = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      defaultSortOrder: "ascend",
    },
    {
      title: "Vehicle",
      key: "vehicle",
      render: (_, driver) => formatVehicle(driver.car),
    },
    {
      title: "Badge IDs",
      key: "badge_ids",
      render: (_, driver) =>
        driver.badge_ids?.length
          ? driver.badge_ids.map((badgeId) => (
              <Tag key={badgeId}>{badgeId}</Tag>
            ))
          : "-",
    },
    {
      title: "Profile",
      key: "profile",
      render: (_, driver) =>
        driver.profile ? (
          <Tag title="The driver configured a personal charging profile, which takes precedence over site profiles.">
            Personal
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Other sites",
      key: "site_ids",
      render: (_, driver) => {
        const otherSites = driver.site_ids.filter((id) => id !== site_id);
        return otherSites.length
          ? otherSites.map((id) => <Tag key={id}>{id}</Tag>)
          : "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, driver) => (
        <Popconfirm
          title={`Delete driver ${driver.email}?`}
          description="This removes the driver from all sites."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            deleteMutation.mutate({ path: { driver_id: driver._id } })
          }
        >
          <DisableForViewer>
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              aria-label="Delete driver"
              loading={
                deleteMutation.isPending &&
                deleteMutation.variables?.path.driver_id === driver._id
              }
            />
          </DisableForViewer>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space direction="vertical" className="w-full">
      <div className="flex justify-between gap-2">
        <Input.Search
          allowClear
          placeholder="Search by email, vehicle or badge"
          className="max-w-sm"
          onChange={(e) => setSearch(e.target.value)}
        />
        <Space>
          <DisableForViewer>
            <Button
              icon={<UploadOutlined />}
              disabled={!partner_id}
              onClick={() => setCsvOpen(true)}
            >
              Upload CSV
            </Button>
          </DisableForViewer>
          <DisableForViewer>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!partner_id}
              onClick={() => setCreateOpen(true)}
            >
              Add driver
            </Button>
          </DisableForViewer>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Failed to load drivers"
          description={extractErrorMessage(error)}
        />
      )}

      <Table
        rowKey="_id"
        size="small"
        loading={isLoading}
        dataSource={filteredDrivers}
        columns={columns}
        locale={{
          emptyText: search
            ? "No drivers match your search."
            : "No drivers yet.",
        }}
      />

      {partner_id && (
        <>
          <CreateDriverModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            site_id={site_id}
            partner_id={partner_id}
          />
          <DriverCsvUploadModal
            open={csvOpen}
            onClose={() => setCsvOpen(false)}
            site_id={site_id}
            partner_id={partner_id}
          />
        </>
      )}
    </Space>
  );
};
